<script lang="ts" module>
  // Tablist — the design-system accessible-tabs primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). A horizontal
  // tab bar following the WAI-ARIA "tabs" pattern: `role="tablist"` with
  // `role="tab"` buttons, roving tabindex (0 on the selected tab, -1 on the
  // rest), and full keyboard nav (Arrow / Home / End move + select; Enter /
  // Space activate the focused tab). A `muted` tab is visually subdued but
  // still clickable (`aria-disabled="false"`). The tab set is a typed prop,
  // never a slot; selection is a typed value. No bespoke per-page tab chrome.
  export interface Tab {
    /** The value committed to `bind:value` when this tab is chosen. */
    value: string;
    /** The visible label. */
    label: string;
    /** Render disabled (skipped by keyboard nav, not selectable). */
    disabled?: boolean;
    /**
     * Render visually muted but STILL enabled + selectable (the "· soon"
     * spoiler tab) — `aria-disabled="false"`, never the `disabled` attribute.
     */
    muted?: boolean;
  }
</script>

<script lang="ts">
  interface Props {
    /** The tabs (2 or more). */
    tabs: Tab[];
    /** The selected tab value (bindable). */
    value: string;
    /** Fired after the selection changes to a new value. */
    onChange?: (value: string) => void;
    /** Accessible group label for the tablist. */
    'aria-label'?: string;
    /** Prefix for each tab's element id (e.g. `al-tab` → `al-tab-respond`). */
    idPrefix?: string;
    class?: string;
  }

  let {
    tabs,
    value = $bindable(),
    onChange,
    'aria-label': ariaLabel,
    idPrefix,
    class: klass = '',
  }: Props = $props();

  // A stable fallback prefix when the caller omits one — minted once (not
  // reactive) so the tab/panel ids stay put; an explicit `idPrefix` reslots it.
  const fallbackPrefix = `vd-tab-${Math.random().toString(36).slice(2, 9)}`;
  const prefix = $derived(idPrefix ?? fallbackPrefix);

  function tabId(tabValue: string): string {
    return `${prefix}-${tabValue}`;
  }

  function select(tabValue: string): void {
    const tab = tabs.find((t) => t.value === tabValue);
    if (!tab || tab.disabled || tabValue === value) return;
    value = tabValue;
    onChange?.(tabValue);
  }

  // Move focus to a tab's button (after a roving-tabindex change).
  function focusTab(tabValue: string): void {
    if (typeof document === 'undefined') return;
    document.getElementById(tabId(tabValue))?.focus();
  }

  // The keyboard-navigable (non-disabled) tab order.
  function navigable(): Tab[] {
    return tabs.filter((t) => !t.disabled);
  }

  function moveTo(tabValue: string): void {
    select(tabValue);
    focusTab(tabValue);
  }

  function onKeydown(event: KeyboardEvent, current: string): void {
    const items = navigable();
    const idx = items.findIndex((t) => t.value === current);
    if (idx === -1) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo(items[(idx + 1) % items.length].value);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo(items[(idx - 1 + items.length) % items.length].value);
    } else if (event.key === 'Home') {
      event.preventDefault();
      moveTo(items[0].value);
    } else if (event.key === 'End') {
      event.preventDefault();
      moveTo(items[items.length - 1].value);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(current);
    }
  }
</script>

<div class="vd-tablist {klass}" role="tablist" aria-label={ariaLabel}>
  {#each tabs as tab (tab.value)}
    <button
      id={tabId(tab.value)}
      type="button"
      role="tab"
      class="vd-tablist__tab"
      class:vd-tablist__tab--active={tab.value === value}
      class:vd-tablist__tab--muted={tab.muted}
      aria-selected={tab.value === value}
      aria-controls={`${prefix}-panel-${tab.value}`}
      aria-disabled={tab.disabled ? true : false}
      disabled={tab.disabled}
      tabindex={tab.value === value ? 0 : -1}
      onclick={() => select(tab.value)}
      onkeydown={(e) => onKeydown(e, tab.value)}
    >
      {tab.label}
    </button>
  {/each}
</div>

<style lang="scss">
  .vd-tablist {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid var(--line);
  }

  .vd-tablist__tab {
    appearance: none;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.55rem 0.9rem;
    margin-bottom: -1px;
    font-family: var(--font-hand);
    font-size: var(--text-guide);
    line-height: 1;
    color: var(--ink-soft);
    cursor: pointer;
    border-radius: 8px 8px 0 0;
    transition:
      color 0.2s ease,
      background 0.2s ease,
      border-color 0.2s ease;

    &:hover:not(:disabled) {
      color: var(--ink);
      background: color-mix(in srgb, var(--accent) 6%, transparent);
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
  }

  .vd-tablist__tab--active {
    color: var(--accent-text);
    border-bottom-color: var(--accent);
  }

  // A muted-but-enabled tab — visually subdued (the "· soon" spoiler), still
  // clickable + keyboard-reachable. Its active state reads in neutral ink so it
  // doesn't claim the practice accent.
  .vd-tablist__tab--muted {
    color: var(--ink-mute);

    &:hover:not(:disabled) {
      color: var(--ink-soft);
    }

    &.vd-tablist__tab--active {
      color: var(--ink-soft);
      border-bottom-color: var(--ink-mute);
    }
  }
</style>
